import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Sparkle, Heart, Lightbulb, ArrowsClockwise, Quotes, Plant, ChartLineUp } from "@phosphor-icons/react";

const EMPTY = { interests: "", strengths: "", improving: "", desired_role: "" };

function Affirmation() {
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/wellbeing/affirmation");
      setData(data);
    } catch (e) {
      console.warn("affirmation load failed:", e?.message || e);
    }
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)]" data-testid="affirmation-card">
      <div className="flex items-start justify-between mb-3">
        <p className="label-tiny flex items-center gap-1.5"><Heart size={12} weight="duotone"/> /today&apos;s reminder</p>
        <button onClick={load} data-testid="affirmation-refresh"
          className="btn-hard bg-white rounded-md p-1.5"><ArrowsClockwise size={14}/></button>
      </div>
      <p className="font-display text-2xl tracking-tight leading-tight" data-testid="affirmation-text">
        <Quotes size={20} weight="duotone" className="inline opacity-40 mr-1"/>
        {data.text}
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rituals">
        {data.rituals?.map((r, i) => (
          <div key={i} className="card-hard rounded-md p-3 bg-white">
            <p className="text-xs font-semibold mb-1">{r.title}</p>
            <p className="text-xs text-[var(--brand-muted)]">{r.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function HireabilityPlan() {
  const [form, setForm] = useState(EMPTY);
  const [plan, setPlan] = useState(null);
  const [running, setRunning] = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(true);

  useEffect(() => {
    api.get("/wellbeing/my-plan").then(({ data }) => {
      if (data?.plan) {
        setPlan(data.plan);
        setForm({ ...EMPTY, ...(data.inputs || {}) });
      }
    }).catch(() => {}).finally(() => setLoadingPrev(false));
    // mount-only: EMPTY is a module constant, api is a stable singleton
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!form.interests.trim() && !form.strengths.trim() && !form.improving.trim()) {
      toast.error("Tell us a little about yourself first");
      return;
    }
    setRunning(true);
    setPlan(null);
    try {
      const { data } = await api.post("/wellbeing/hireability-plan", form);
      setPlan(data);
      toast.success("Your plan is ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plan generation failed");
    } finally { setRunning(false); }
  };

  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/hireability quiz</p>
        <h3 className="font-display text-2xl tracking-tight mb-1">where are you headed?</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">
          {loadingPrev ? "loading…" : plan ? "edit any field and re-generate to refresh your plan." : "answer 4 quick prompts. we&apos;ll build you a 5-step plan to close the gap."}
        </p>
        <div className="space-y-4">
          <div>
            <label className="label-tiny block mb-1.5">desired role (e.g. junior data analyst at a charity)</label>
            <input data-testid="wb-desired-role" value={form.desired_role}
              onChange={(e) => setForm({...form, desired_role: e.target.value})}
              className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
          </div>
          <div>
            <label className="label-tiny block mb-1.5">what are you actually interested in?</label>
            <textarea data-testid="wb-interests" value={form.interests}
              onChange={(e) => setForm({...form, interests: e.target.value})}
              placeholder="film, climate, finance, content design — anything"
              className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[70px]"/>
          </div>
          <div>
            <label className="label-tiny block mb-1.5">what are you genuinely good at?</label>
            <textarea data-testid="wb-strengths" value={form.strengths}
              onChange={(e) => setForm({...form, strengths: e.target.value})}
              placeholder="writing emails people actually read, explaining tricky things, breaking down spreadsheets"
              className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[70px]"/>
          </div>
          <div>
            <label className="label-tiny block mb-1.5">what do you feel you&apos;re lacking?</label>
            <textarea data-testid="wb-improving" value={form.improving}
              onChange={(e) => setForm({...form, improving: e.target.value})}
              placeholder="no portfolio yet, no real-world projects, networking feels horrible"
              className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[70px]"/>
          </div>
        </div>
        <button onClick={generate} disabled={running} data-testid="wb-generate"
          className="mt-4 btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {running ? <><ArrowsClockwise size={16} className="animate-spin"/> thinking…</> : <><Sparkle size={16} weight="bold"/> {plan ? "refresh plan" : "build my plan"}</>}
        </button>
      </div>

      {plan && (
        <div className="space-y-4" data-testid="wb-plan-result">
          <div className="card-hard rounded-md p-5 bg-[var(--pastel-yellow)]">
            <p className="label-tiny mb-1">/your plan</p>
            <h3 className="font-display text-2xl tracking-tight" data-testid="wb-plan-headline">{plan.headline}</h3>
            <p className="text-sm mt-2 text-[var(--brand-muted)]">{plan.summary}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wb-plan-actions">
            {(plan.actions || []).map((a, i) => (
              <div key={i} className="card-hard rounded-md p-4 bg-white">
                <div className="flex items-start justify-between mb-2">
                  <p className="font-display text-base tracking-tight">{i+1}. {a.title}</p>
                  <span className="font-mono-d text-[10px] px-1.5 py-0.5 rounded card-hard bg-[var(--pastel-mint)]">{a.effort}</span>
                </div>
                <p className="text-xs text-[var(--brand-muted)] mb-2"><b className="text-[var(--brand-ink)]">why:</b> {a.why}</p>
                <p className="text-xs text-[var(--brand-muted)]"><b className="text-[var(--brand-ink)]">how:</b> {a.how}</p>
              </div>
            ))}
          </div>

          {plan.kind_truth && (
            <div className="card-hard rounded-md p-4 bg-[var(--pastel-peach)]" data-testid="wb-kind-truth">
              <p className="label-tiny mb-1 flex items-center gap-1.5"><Lightbulb size={12} weight="duotone"/> /honest take</p>
              <p className="text-sm">{plan.kind_truth}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommunityInsights() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(true);

  const load = async (t = threshold) => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/insights/companies?min_contributors=${t}`),
        api.get("/insights/my-contribution-stats"),
      ]);
      setData(a.data);
      setMe(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(threshold); }, [threshold]);

  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)] mb-6">
        <p className="label-tiny mb-2">/your contribution</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm">
            you&apos;ve added <b data-testid="my-pm-count">{me?.postmortems ?? 0}</b> post-mortems and{" "}
            <b data-testid="my-ca-count">{me?.culture_analyses ?? 0}</b> culture analyses. every entry helps other applicants.
          </p>
          <p className="font-mono-d text-xs text-[var(--brand-muted)]">privacy floor: {me?.min_contributors_threshold || 3}+ users per company</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="label-tiny">/community ratings — companies with {threshold}+ contributors</p>
        <select value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}
          data-testid="insights-threshold"
          className="card-hard rounded-md px-2 py-1 text-xs bg-white">
          <option value={1}>show all (testing)</option>
          <option value={2}>2+ contributors</option>
          <option value={3}>3+ contributors</option>
          <option value={5}>5+ contributors</option>
        </select>
      </div>

      {loading ? (
        <p className="font-mono-d text-sm text-[var(--brand-muted)]">loading…</p>
      ) : (data?.companies?.length || 0) === 0 ? (
        <div className="card-hard rounded-md p-10 bg-white text-center" data-testid="insights-empty">
          <ChartLineUp size={36} weight="duotone" className="mx-auto mb-3 text-[var(--brand-muted)]"/>
          <p className="font-display text-2xl tracking-tight">no ratings yet at this threshold.</p>
          <p className="text-sm text-[var(--brand-muted)] mt-2">
            the more JobVault users add post-mortems and analyse job ads, the richer this view gets. you&apos;re early.
          </p>
        </div>
      ) : (
        <div className="card-hard rounded-md bg-white overflow-x-auto" data-testid="insights-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-[1.5px] border-[var(--brand-ink)]">
                <th className="text-left p-3 label-tiny">/company</th>
                <th className="text-left p-3 label-tiny">contributors</th>
                <th className="text-left p-3 label-tiny">avg interview rating</th>
                <th className="text-left p-3 label-tiny">interviewer vibe</th>
                <th className="text-left p-3 label-tiny">process speed</th>
                <th className="text-left p-3 label-tiny">recommend %</th>
                <th className="text-left p-3 label-tiny">culture stars</th>
              </tr>
            </thead>
            <tbody>
              {data.companies.map((c) => (
                <tr key={c.key} className="border-b border-[var(--brand-ink)]/10" data-testid={`insights-row-${c.key}`}>
                  <td className="p-3 font-medium">{c.company}</td>
                  <td className="p-3 font-mono-d text-xs">{c.contributors}</td>
                  <td className="p-3 font-mono-d">{c.avg_rating ?? "—"}{c.avg_rating ? "/5" : ""}</td>
                  <td className="p-3 font-mono-d">{c.avg_interviewer_vibe ?? "—"}{c.avg_interviewer_vibe ? "/5" : ""}</td>
                  <td className="p-3 font-mono-d">{c.avg_process_speed ?? "—"}{c.avg_process_speed ? "/5" : ""}</td>
                  <td className="p-3 font-mono-d">{c.recommend_percent !== null ? `${c.recommend_percent}%` : "—"}</td>
                  <td className="p-3 font-mono-d">{c.avg_culture_stars ?? "—"}{c.avg_culture_stars ? "/5" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Wellbeing() {
  const [tab, setTab] = useState("plan");
  return (
    <AppShell title="Wellbeing">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-[var(--brand-muted)] max-w-2xl">
          job hunting is hard. this is the corner where we look after you, not just your CV.
        </p>
        <div className="card-hard rounded-md p-1 flex" data-testid="wellbeing-tabs">
          <button onClick={() => setTab("plan")} data-testid="tab-wb-plan"
            className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 ${tab === "plan" ? "bg-[var(--brand-ink)] text-white" : ""}`}>
            <Plant size={14}/> hireability plan
          </button>
          <button onClick={() => setTab("insights")} data-testid="tab-wb-insights"
            className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 ${tab === "insights" ? "bg-[var(--brand-ink)] text-white" : ""}`}>
            <ChartLineUp size={14}/> community insights
          </button>
        </div>
      </div>

      <div className="mb-6">
        <Affirmation/>
      </div>

      {tab === "plan" ? <HireabilityPlan/> : <CommunityInsights/>}
    </AppShell>
  );
}
